/** Browser-only, original-pixel region geometry. No canvas, segmentation, or AI. */
export interface SourceRegionBox { x:number; y:number; width:number; height:number }
export interface WorkbenchSourceRegion { id:string; name:string; layerName:string; kind:'TEXT'|'SUBJECT'|'GRAPHICS'; boxes:SourceRegionBox[]; layerId?:string }
export interface SourceRegionManifest { version:1; coordinateSystem:'SOURCE_NORMALIZED'; regions:WorkbenchSourceRegion[] }
export const SOURCE_REGION_LIMITS={regions:24,boxesPerRegion:12,jsonBytes:65_536,xmpChars:131_072,pixels:4_194_304,boxVisits:67_108_864,outputPixels:33_554_432} as const
export const SOURCE_REGION_NAMESPACE='https://yihang.app/ns/source-regions/1.0/'
export const WORKBENCH_BACKGROUND_FILL='EXISTING_AI_CLEAN_PLATE_V1' as const
export const WORKBENCH_BACKGROUND_NAME='干净背景（AI 补全）'
function fail(message:string):never {throw new Error(message)}
const object=(v:unknown):v is Record<string,unknown>=>!!v&&typeof v==='object'&&!Array.isArray(v)
const text=(v:unknown,max:number):v is string=>typeof v==='string'&&v.length>0&&v.length<=max&&v.trim()===v&&!/[\u0000-\u001f]/.test(v)
const keys=(v:Record<string,unknown>,allowed:string[])=>Object.keys(v).every(k=>allowed.includes(k))
export function validateSourceRegionManifest(value:unknown):SourceRegionManifest {
 if(!object(value)||!keys(value,['version','coordinateSystem','regions'])||value.version!==1||value.coordinateSystem!=='SOURCE_NORMALIZED'||
  !Array.isArray(value.regions)||value.regions.length<1||value.regions.length>SOURCE_REGION_LIMITS.regions)fail('区域清单格式或数量无效')
 const ids=new Set<string>(),names=new Set<string>()
 const regions=(value.regions as unknown[]).map(v=>{
  if(!object(v)||!keys(v,['id','name','layerName','kind','boxes','layerId'])||!text(v.id,64)||!text(v.name,200)||!text(v.layerName,100)||
   !['TEXT','SUBJECT','GRAPHICS'].includes(String(v.kind))||!Array.isArray(v.boxes)||!v.boxes.length||v.boxes.length>SOURCE_REGION_LIMITS.boxesPerRegion)fail('区域名称、类型或矩形数量无效')
  const r=v as Record<string,unknown>
  if(ids.has(r.id as string)||names.has(r.layerName as string))fail('区域 ID 或图层名称重复')
  ids.add(r.id as string);names.add(r.layerName as string)
  const boxes=(r.boxes as unknown[]).map(b=>{
   if(!object(b)||!keys(b,['x','y','width','height'])||![b.x,b.y,b.width,b.height].every(n=>typeof n==='number'&&Number.isFinite(n))||
    (b.x as number)<0||(b.y as number)<0||(b.width as number)<=0||(b.height as number)<=0||
    (b.x as number)+(b.width as number)>1+Number.EPSILON*4||(b.y as number)+(b.height as number)>1+Number.EPSILON*4)fail('区域矩形必须在原图范围内')
   return {x:b.x as number,y:b.y as number,width:b.width as number,height:b.height as number}
  })
  return {id:r.id as string,name:r.name as string,layerName:r.layerName as string,kind:r.kind as WorkbenchSourceRegion['kind'],boxes}
 })
 const result:SourceRegionManifest={version:1,coordinateSystem:'SOURCE_NORMALIZED',regions}
 if(new TextEncoder().encode(JSON.stringify(result)).length>SOURCE_REGION_LIMITS.jsonBytes)fail('区域清单超过安全大小')
 return result
}
export function sourceRegionsXmp(manifest:SourceRegionManifest,existing?:string):string {
 const json=new TextEncoder().encode(JSON.stringify(validateSourceRegionManifest(manifest)))
 let binary='';for(const b of json)binary+=String.fromCharCode(b)
 const element=`<yihang:sourceRegions>${btoa(binary)}</yihang:sourceRegions>`
 const description=`<rdf:Description rdf:about="" xmlns:yihang="${SOURCE_REGION_NAMESPACE}">${element}</rdf:Description>`
 let xmp:string
 if(!existing)xmp=`<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">${description}</rdf:RDF></x:xmpmeta>`
 else {
  if(existing.length>SOURCE_REGION_LIMITS.xmpChars)fail('PSD XMP 超过安全大小')
  if(existing.includes('<yihang:sourceRegions>')){
   readSourceRegionsXmp(existing) // reject ambiguous/malformed own metadata
   xmp=existing.replace(/<yihang:sourceRegions>[A-Za-z0-9+/=]*<\/yihang:sourceRegions>/,element)
  } else if(existing.includes('</rdf:RDF>'))xmp=existing.replace('</rdf:RDF>',description+'</rdf:RDF>')
  else fail('现有 PSD 元数据不支持安全写入区域清单')
 }
 if(xmp.length>SOURCE_REGION_LIMITS.xmpChars)fail('PSD XMP 超过安全大小')
 return xmp
}
export function readSourceRegionsXmp(xmp:unknown):SourceRegionManifest|undefined {
 if(xmp===undefined||xmp===null||xmp==='')return undefined
 if(typeof xmp!=='string'||xmp.length>SOURCE_REGION_LIMITS.xmpChars)fail('PSD XMP 超过安全大小')
 if(!xmp.includes('yihang:sourceRegions'))return undefined
 if(!xmp.includes(`xmlns:yihang="${SOURCE_REGION_NAMESPACE}"`)&&!xmp.includes(`xmlns:yihang='${SOURCE_REGION_NAMESPACE}'`))fail('区域清单命名空间无效')
 if(/<!DOCTYPE|<!ENTITY/i.test(xmp))fail('区域清单不能包含外部实体')
 const matches=[...xmp.matchAll(/<yihang:sourceRegions>([A-Za-z0-9+/=]*)<\/yihang:sourceRegions>/g)]
 if(matches.length!==1||(xmp.match(/yihang:sourceRegions/g)?.length??0)!==2)fail('PSD 区域清单重复或损坏')
 const encoded=matches[0]![1]!
 if(encoded.length>Math.ceil(SOURCE_REGION_LIMITS.jsonBytes/3)*4||encoded.length%4!==0||!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(encoded))fail('PSD 区域清单编码无效')
 try {
  const binary=atob(encoded);if(btoa(binary)!==encoded)fail('PSD 区域清单编码无效')
  const bytes=Uint8Array.from(binary,c=>c.charCodeAt(0))
  if(bytes.length>SOURCE_REGION_LIMITS.jsonBytes)fail('区域清单超过安全大小')
  return validateSourceRegionManifest(JSON.parse(new TextDecoder('utf-8',{fatal:true}).decode(bytes)))
 }catch{ return fail('PSD 区域清单无法解析') }
}

/** Independent provenance: sourceRegions v1 remains unchanged. */
export function readWorkbenchBackgroundFill(xmp:unknown):typeof WORKBENCH_BACKGROUND_FILL|undefined {
 if(typeof xmp!=='string'||!xmp.includes('yihang:backgroundFill'))return undefined
 if(!readSourceRegionsXmp(xmp))fail('补底标记缺少有效区域清单')
 const matches=[...xmp.matchAll(/<yihang:backgroundFill>([^<]*)<\/yihang:backgroundFill>/g)]
 if(matches.length!==1||(xmp.match(/yihang:backgroundFill/g)?.length??0)!==2||matches[0]![1]!==WORKBENCH_BACKGROUND_FILL)fail('PSD 补底标记无效')
 return WORKBENCH_BACKGROUND_FILL
}
export function workbenchBackgroundFillXmp(manifest:SourceRegionManifest,existing?:string):string {
 const xmp=sourceRegionsXmp(manifest,existing)
 if(readWorkbenchBackgroundFill(xmp))return xmp
 const result=xmp.replace('</yihang:sourceRegions>',`</yihang:sourceRegions><yihang:backgroundFill>${WORKBENCH_BACKGROUND_FILL}</yihang:backgroundFill>`)
 if(result.length>SOURCE_REGION_LIMITS.xmpChars)fail('PSD XMP 超过安全大小')
 return result
}

interface PixelBox {left:number;top:number;right:number;bottom:number}
export interface SourceRegionPixels {id:string;name:string;kind:WorkbenchSourceRegion['kind']|'BACKGROUND';left:number;top:number;width:number;height:number;data:Uint8Array;ownership:Uint8Array}
const edge=(v:number,n:number)=>{const s=v*n,h=Math.round(s*2)/2;return Math.round(Math.abs(s-h)<=Number.EPSILON*Math.max(1,Math.abs(s))*8?h:s)}
function unionArea(boxes:PixelBox[]):number {
 const xs=[...new Set(boxes.flatMap(b=>[b.left,b.right]))].sort((a,b)=>a-b);let area=0
 for(let i=1;i<xs.length;i++){
  const left=xs[i-1]!,right=xs[i]!,ys=boxes.filter(b=>b.left<right&&b.right>left).sort((a,b)=>a.top-b.top||a.bottom-b.bottom)
  let end=-1,covered=0;for(const b of ys){covered+=Math.max(0,b.bottom-Math.max(end,b.top));end=Math.max(end,b.bottom)}
  area+=(right-left)*covered
 }return area
}
export function partitionWorkbenchSource(source:{width:number;height:number;data:Uint8Array|Uint8ClampedArray},regions:readonly WorkbenchSourceRegion[]) {
 const {width,height,data}=source,count=width*height
 if(!Number.isSafeInteger(width)||!Number.isSafeInteger(height)||width<1||height<1||width>8192||height>8192||count>SOURCE_REGION_LIMITS.pixels||
  !(data instanceof Uint8Array||data instanceof Uint8ClampedArray)||data.length!==count*4)fail('原图像素超过区域编辑安全范围')
 const manifest=validateSourceRegionManifest({version:1,coordinateSystem:'SOURCE_NORMALIZED',regions})
 let visits=0
 const checked=manifest.regions.map((region,index)=>{
  const boxes=region.boxes.map(b=>{
   const p={left:edge(b.x,width),top:edge(b.y,height),right:edge(Math.min(1,b.x+b.width),width),bottom:edge(Math.min(1,b.y+b.height),height)}
   if(p.right<=p.left||p.bottom<=p.top)fail('区域矩形小于一个原图像素')
   visits+=(p.right-p.left)*(p.bottom-p.top);if(visits>SOURCE_REGION_LIMITS.boxVisits)fail('区域切分计算量超过安全限制')
   return p
  });return {region,index,boxes,area:unionArea(boxes)}
 })
 const ownerMap=new Uint16Array(count)
 const order=[...checked].sort((a,b)=>(a.region.kind==='SUBJECT'?0:1)-(b.region.kind==='SUBJECT'?0:1)||b.area-a.area||(a.region.id<b.region.id?-1:a.region.id>b.region.id?1:0))
 for(const entry of order)for(const b of entry.boxes)for(let y=b.top;y<b.bottom;y++)ownerMap.fill(entry.index+1,y*width+b.left,y*width+b.right)
 const bounds=Array.from({length:checked.length+1},()=>({left:width,top:height,right:0,bottom:0,count:0}))
 for(let p=0;p<count;p++){const b=bounds[ownerMap[p]!]!,x=p%width,y=Math.floor(p/width);b.left=Math.min(b.left,x);b.top=Math.min(b.top,y);b.right=Math.max(b.right,x+1);b.bottom=Math.max(b.bottom,y+1);b.count++}
 const hidden=checked.filter(e=>bounds[e.index+1]!.count===0)
 if(hidden.length)fail('区域完全被其他区域覆盖，请调整或合并：'+hidden.map(e=>e.region.name).join('、'))
 const outputPixels=bounds.reduce((n,b)=>n+(b.count?(b.right-b.left)*(b.bottom-b.top):0),0)
 if(outputPixels>SOURCE_REGION_LIMITS.outputPixels)fail('图层素材总像素超过安全限制')
 const make=(label:number):SourceRegionPixels|null=>{
  const b=bounds[label]!;if(!b.count)return null
  const region=checked[label-1]?.region,w=b.right-b.left,h=b.bottom-b.top
  return {id:region?.id??'__source_background__',name:region?.layerName??'剩余背景（移动后保留透明空缺）',kind:region?.kind??'BACKGROUND',left:b.left,top:b.top,width:w,height:h,data:new Uint8Array(w*h*4),ownership:new Uint8Array(w*h)}
 }
 const outputs=bounds.map((_,i)=>make(i))
 for(let p=0;p<count;p++){const layer=outputs[ownerMap[p]!]!,x=p%width,y=Math.floor(p/width),q=(y-layer.top)*layer.width+x-layer.left;layer.ownership[q]=1;layer.data.set(data.subarray(p*4,p*4+4),q*4)}
 return {regions:outputs.slice(1) as SourceRegionPixels[],background:outputs[0]??null,ownerMap,manifest}
}
export function mergeWorkbenchSourceRegions(regions:readonly WorkbenchSourceRegion[],selectedIds:readonly string[]):WorkbenchSourceRegion[] {
 const input=validateSourceRegionManifest({version:1,coordinateSystem:'SOURCE_NORMALIZED',regions}).regions
 const selected=new Set(selectedIds)
 if(selected.size<2||selected.size!==selectedIds.length||[...selected].some(id=>!input.some(r=>r.id===id)))fail('请至少选择两个不同的有效区域')
 const chosen=input.filter(r=>selected.has(r.id)),first=chosen[0]!,boxes:SourceRegionBox[]=[]
 for(const r of chosen)for(const box of r.boxes)if(!boxes.some(b=>JSON.stringify(b)===JSON.stringify(box)))boxes.push({...box})
 if(boxes.length>SOURCE_REGION_LIMITS.boxesPerRegion)fail('合并后超过 12 个矩形，请先简化区域')
 const kind=chosen.some(r=>r.kind==='SUBJECT')?'SUBJECT':chosen.some(r=>r.kind==='GRAPHICS')?'GRAPHICS':'TEXT'
 const merged={...first,name:chosen.map(r=>r.name).join(' + ').slice(0,200),kind,boxes} as WorkbenchSourceRegion
 return input.flatMap(r=>r.id===first.id?[merged]:selected.has(r.id)?[]:[r])
}
